// The Co-pilot nudge: a small card in the corner, not a dialog. It never takes
// focus, never dims the page and never blocks the list, and the full quiz only
// opens when the visitor clicks it. Rules, in order:
//
//  1. Only people who are browsing. It waits for 20 seconds on the page AND a
//     sign of intent: a search, a filter, opening a job, or scrolling well
//     into the list. Someone who lands, reads and leaves never sees it.
//  2. Sooner when they are stuck. A search with 3 or fewer results shows it
//     after 8 seconds, with copy about widening the net.
//  3. Never mid-typing (it waits for the field to lose focus), never while the
//     page has its own dialog open (`paused`), never on a visit that came from
//     an email, never for someone who finished the quiz.
//  4. At most twice per person, 14 days apart. Clicking it, or dismissing it
//     on the second showing, ends it for good.
//
// Every showing, open and dismissal is logged through siteEvents so the nudge
// can be judged on completions, not impressions.
import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import QuizModal from "./QuizModal";
import { PROFILE_KEY } from "./useQuizData";
import { SEEN_KEY, fromEmail } from "./useQuizTrigger";
import { siteEvents } from "@/lib/siteEvents";
import "./copilot-nudge.css";

const NUDGE_KEY = "af_copilot_nudge";
const DWELL_MS = 20_000;
const STUCK_MS = 8_000;
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
const MAX_SHOWS = 2;
const SCROLL_INTENT_PX = 900;

type NudgeRecord = { count: number; until: number };

function readRecord(): NudgeRecord | null {
  try {
    if (localStorage.getItem(PROFILE_KEY)) return null; // finished the quiz: nothing to sell
    const raw = localStorage.getItem(NUDGE_KEY);
    if (raw) return JSON.parse(raw) as NudgeRecord;
    // The old full-screen prompt counts as one showing.
    return { count: localStorage.getItem(SEEN_KEY) ? 1 : 0, until: 0 };
  } catch {
    return null; // no storage means no way to honour the limits, so never show
  }
}

function writeRecord(r: NudgeRecord) {
  try {
    localStorage.setItem(NUDGE_KEY, JSON.stringify(r));
    localStorage.setItem(SEEN_KEY, new Date().toISOString()); // keeps the job-page dialog quiet too
  } catch { /* the nudge still closes normally */ }
}

function eligible(): boolean {
  if (fromEmail()) return false;
  const r = readRecord();
  return !!r && r.count < MAX_SHOWS && Date.now() >= r.until;
}

const typing = () => {
  const el = document.activeElement as HTMLElement | null;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
};

export type NudgeContext = { title?: string; location?: string };
export type NudgeHandle = {
  /** Something that shows intent: a search, a filter change, a job opened. */
  signal: (kind: "search" | "filter" | "job_open") => void;
  /** A search came back thin. */
  results: (count: number, query: string) => void;
};

type Props = {
  source: string;
  context?: NudgeContext;
  paused?: boolean;
  /** Seconds of dwell before a browsing visitor sees it (default 20). */
  dwellMs?: number;
  /** Job pages have a fixed Apply bar on phones; the nudge must never cover it. */
  desktopOnly?: boolean;
};

const CopilotNudge = forwardRef<NudgeHandle, Props>(function CopilotNudge({ source, context, paused = false, dwellMs = DWELL_MS, desktopOnly = false }, ref) {
  const [shown, setShown] = useState<null | { reason: "browsing" | "stuck"; query?: string; count?: number }>(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const start = useRef(Date.now());
  const intent = useRef(false);
  const stuck = useRef<null | { query: string; count: number }>(null);
  const done = useRef(!eligible());

  const tryShow = useCallback(() => {
    if (done.current || paused || typing()) return;
    if (desktopOnly && window.innerWidth <= 980) return;
    const elapsed = Date.now() - start.current;
    let next: typeof shown = null;
    if (stuck.current && elapsed >= STUCK_MS) next = { reason: "stuck", query: stuck.current.query, count: stuck.current.count };
    else if (intent.current && elapsed >= dwellMs) next = { reason: "browsing" };
    if (!next || !eligible()) return;
    done.current = true;
    const r = readRecord() ?? { count: 0, until: 0 };
    writeRecord({ count: r.count + 1, until: Date.now() + SNOOZE_MS });
    setShown(next);
    siteEvents.act("copilot_nudge_shown", { source, reason: next.reason, showing: r.count + 1 });
  }, [paused, source, dwellMs, desktopOnly]);

  useImperativeHandle(ref, () => ({
    signal: () => { intent.current = true; },
    results: (count, query) => {
      if (query.trim() && count <= 3) { stuck.current = { query: query.trim(), count }; intent.current = true; }
    },
  }), []);

  useEffect(() => {
    if (done.current) return;
    const onScroll = () => { if (window.scrollY > SCROLL_INTENT_PX) intent.current = true; };
    window.addEventListener("scroll", onScroll, { passive: true });
    const tick = window.setInterval(tryShow, 1000);
    return () => { window.removeEventListener("scroll", onScroll); window.clearInterval(tick); };
  }, [tryShow]);

  const dismiss = () => {
    setShown(null);
    siteEvents.act("copilot_nudge_dismiss", { source });
  };
  const open = () => {
    setShown(null);
    const r = readRecord() ?? { count: 0, until: 0 };
    writeRecord({ count: MAX_SHOWS, until: r.until }); // they said yes once; never nudge again
    siteEvents.act("copilot_nudge_open", { source });
    setQuizOpen(true);
  };

  const title = context?.title?.trim();
  const place = context?.location?.trim();
  const heading = shown?.reason === "stuck"
    ? `${shown.count ? `Only ${shown.count} ${shown.count === 1 ? "match" : "matches"}` : "No matches"} for “${shown.query}”`
    : title
      ? `Looking for ${title} roles${place ? ` in ${place}` : ""}?`
      : "Not sure what to search for?";
  const body = shown?.reason === "stuck"
    ? "Tell us what you are after and we will widen the net, including roles that never reach the job boards."
    : "Answer eleven questions and we build your shortlist, including roles that never reach the job boards.";

  return (
    <>
      {shown && !paused && (
        <aside className="cp-nudge" aria-label="Job Search Co-pilot" aria-live="polite">
          <button type="button" className="cp-nudge__x" aria-label="Dismiss" onClick={dismiss}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
          <p className="cp-nudge__k">Job Search Co-pilot</p>
          <p className="cp-nudge__h">{heading}</p>
          <p className="cp-nudge__b">{body}</p>
          <div className="cp-nudge__row">
            <button type="button" className="cp-nudge__go" onClick={open}>Build my shortlist</button>
            <button type="button" className="cp-nudge__later" onClick={dismiss}>Not now</button>
          </div>
          <p className="cp-nudge__meta">About 2 minutes. No account needed.</p>
        </aside>
      )}
      {quizOpen && <QuizModal open onClose={() => setQuizOpen(false)} source={`nudge-${source}`} />}
    </>
  );
});

export default CopilotNudge;
